import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Mail, Phone, Building, Search, Printer } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { formatDate } from '../../lib/utils';
import { clientService } from '../../lib/database/clients';
import { CreateClientModal } from './CreateClientModal';
import { EditClientModal } from './EditClientModal';
import { PrintableClient } from '../print/PrintableClient';

export const ClientsList: React.FC = () => {
  const [clients, setClients] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [showPrintView, setShowPrintView] = useState(false);
  const [clientToPrint, setClientToPrint] = useState<any>(null);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const { data } = await clientService.getClients();
      if (data) {
        setClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleCreateSuccess = () => {
    fetchClients(); // Refresh the list
  };

  const handleEditSuccess = () => {
    fetchClients(); // Refresh the list
    setSelectedClient(null);
  };

  const handleEditClient = (client: any) => {
    setSelectedClient(client);
    setShowEditModal(true);
  };

  const handlePrintClient = (client: any) => {
    setClientToPrint(client);
    setShowPrintView(true);
    setTimeout(() => {
      window.print();
      setShowPrintView(false);
    }, 100);
  };

  const handleDeleteClient = async (clientId: string) => {
    if (window.confirm('Are you sure you want to delete this client? This will also delete all associated invoices and quotes.')) {
      try {
        await clientService.deleteClient(clientId);
        fetchClients();
      } catch (error) {
        console.error('Error deleting client:', error);
        alert('Failed to delete client. Please try again.');
      }
    }
  };

  const filteredClients = clients.filter(client => {
    const searchLower = searchTerm.toLowerCase();
    return client.name?.toLowerCase().includes(searchLower) ||
           client.email?.toLowerCase().includes(searchLower) ||
           client.company?.toLowerCase().includes(searchLower) ||
           client.phone?.toLowerCase().includes(searchLower) ||
           client.address?.toLowerCase().includes(searchLower) ||
           client.gstin?.toLowerCase().includes(searchLower);
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="bg-white p-6 rounded-xl h-96"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Clients</h1>
            <p className="text-gray-600 mt-2">Manage your client database</p>
          </div>
          <Button 
            icon={Plus} 
            className="shadow-lg"
            onClick={() => setShowCreateModal(true)}
          >
            Add Client
          </Button>
        </div>

        {/* Search */}
        <Card padding="sm">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search clients by name, email, company, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full text-sm"
            />
          </div>
        </Card>

        {/* Clients Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.length === 0 ? (
            <div className="col-span-full">
              <Card className="text-center py-12">
                <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {searchTerm ? 'No clients found' : 'No clients yet'}
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm 
                    ? 'Try adjusting your search terms or clear the search to see all clients'
                    : 'Get started by adding your first client'
                  }
                </p>
                {!searchTerm && (
                  <Button 
                    icon={Plus}
                    onClick={() => setShowCreateModal(true)}
                  >
                    Add Your First Client
                  </Button>
                )}
              </Card>
            </div>
          ) : (
            filteredClients.map((client) => (
              <Card key={client.id} hover className="relative">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Building className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      icon={Printer}
                      onClick={() => handlePrintClient(client)}
                    >
                      Print
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      icon={Edit}
                      onClick={() => handleEditClient(client)}
                    >
                      Edit
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      icon={Trash2}
                      onClick={() => handleDeleteClient(client.id)}
                      className="text-error-600 hover:text-error-700"
                    >
                      Delete
                    </Button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{client.name}</h3>
                    {client.company && (
                      <p className="text-gray-600">{client.company}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Mail className="w-4 h-4" />
                      <span>{client.email}</span>
                    </div>
                    
                    {client.phone && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Phone className="w-4 h-4" />
                        <span>{client.phone}</span>
                      </div>
                    )}
                  </div>

                  {client.address && (
                    <p className="text-sm text-gray-600 line-clamp-2">{client.address}</p>
                  )}

                  {client.gstin && (
                    <div className="bg-gray-50 px-3 py-2 rounded-lg">
                      <p className="text-xs text-gray-500">GSTIN</p>
                      <p className="text-sm font-medium text-gray-900">{client.gstin}</p>
                    </div>
                  )}

                  <div className="pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      Added {formatDate(client.created_at)}
                    </p>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Search Results Summary */}
        {searchTerm && (
          <Card padding="sm">
            <div className="text-center text-sm text-gray-600">
              Showing {filteredClients.length} of {clients.length} clients matching "{searchTerm}"
              {filteredClients.length !== clients.length && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="ml-2 text-primary-600 hover:text-primary-700 font-medium"
                >
                  Clear search
                </button>
              )}
            </div>
          </Card>
        )}

        {/* Create Client Modal */}
        <CreateClientModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />

        {/* Edit Client Modal */}
        <EditClientModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedClient(null);
          }}
          onSuccess={handleEditSuccess}
          client={selectedClient}
        />
      </div>

      {/* Printable Version */}
      <PrintableClient client={clientToPrint} isVisible={showPrintView} />
    </>
  );
};